/**
 * Dynamic operating-account selector (person → N accounts).
 * Replaces the old investimento / individualizada toggle.
 *
 * variant:
 * - "auto" (default): segmented pills when ≤3 accounts, native select when more
 * - "select": always a compact dropdown (preferred on IB home with many accounts)
 */

import { dedupeAccountsByStableId } from "../../../Services/baasAccountsListApi";

const formatOptionLabel = (account) => {
  const parts = [account.label || account.id];
  if (account.balanceHint) parts.push(account.balanceHint);
  if (account.secondaryLabel) parts.push(account.secondaryLabel);
  return parts.join(" · ");
};

export const TransferAccountSelector = ({
  accounts = [],
  value,
  onChange,
  ariaLabel = "Conta",
  disabled = false,
  loading = false,
  emptyLabel = "Nenhuma conta disponível",
  variant = "auto",
}) => {
  // Ensure unique rows and a single active segment even if a caller passes dups.
  const uniqueAccounts = dedupeAccountsByStableId(accounts);
  const selectedId = value != null && value !== "" ? String(value) : "";
  const useSelect = variant === "select" || uniqueAccounts.length > 3;

  if (loading) {
    return (
      <div
        className="home-account-selector home-account-selector--select home-account-selector--loading"
        role="status"
        aria-label={ariaLabel}
      >
        <span className="home-account-selector__select home-account-selector__select--placeholder">
          Carregando contas…
        </span>
      </div>
    );
  }

  if (!uniqueAccounts.length) {
    return (
      <div
        className="home-account-selector home-account-selector--select"
        role="group"
        aria-label={ariaLabel}
      >
        <span className="home-account-selector__select home-account-selector__select--placeholder">
          {emptyLabel}
        </span>
      </div>
    );
  }

  if (useSelect) {
    return (
      <div className="home-account-selector home-account-selector--select" aria-label={ariaLabel}>
        <select
          className="home-account-selector__select"
          value={selectedId}
          disabled={disabled}
          aria-label={ariaLabel}
          onChange={(event) => onChange?.(event.target.value)}
        >
          {uniqueAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {formatOptionLabel(account)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div
      className="home-account-selector home-account-selector--transfer"
      role="group"
      aria-label={ariaLabel}
    >
      {uniqueAccounts.map((account) => {
        const accountId = String(account.id);
        const isActive = selectedId !== "" && selectedId === accountId;
        return (
          <button
            key={accountId}
            type="button"
            className={`home-account-selector__btn ${
              isActive ? "home-account-selector__btn--active" : ""
            }`}
            aria-pressed={isActive}
            disabled={disabled}
            title={formatOptionLabel(account)}
            onClick={() => onChange?.(accountId)}
          >
            <span className="home-account-selector__btn-label">{account.label}</span>
            {account.balanceHint ? (
              <span className="home-account-selector__btn-hint">{account.balanceHint}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};
